import { usePlanBuyAction } from '../../../../../execution/buy/usePlanBuyAction'
import { showAsFormattedRupeeAmount } from '../../../../../display-values/lumpsumPlanAmount'
import {
  LumpsumAllocationCategoryExecutionStatus,
  type LumpsumAllocationBuyRecommendation
} from '../../../../api/allocation.types'
import { AllocationMovementToggle, PlanCategoryBucketKey } from '../../../../api/types'
import { useOpenPlanRecommendationBottomSheet } from '../../../../plan-bottom-sheet/open/useOpenPlanRecommendationBottomSheet'
import { useLumpsumPlansContext } from '../../../LumpsumPlansProvider'
import { AllocationFundCardTrailingLayout } from '../../../lumpsum-ui-components/AllocationFundCardTrailingLayout'
import {
  PlansFundCard,
  type PlanFundCardCta,
  type PlanFundCardInsight
} from '../../../lumpsum-ui-components/PlansFundCard'
import { buildAllocationInsightsFromTags, toBuyAllocationInsightVisual } from '../allocationInsightTags'
import { planPercentageLabel } from '../../../../../display-values/lumpsumPlanPercentage'
import { formatFundIdealRangeTrailingLabel } from '../../../../../utils/lumpsum/planListUtils'
import { PlansFundRole, PlansScreen } from '../../../../../analytics/plansEvents'
import { trackPlansFundCardCta, trackPlansFundCardOpen } from '../../../../../analytics/plansTrack'

type AllocationBuyFundCardProps = {
  recommendation: LumpsumAllocationBuyRecommendation
  categoryName: string
  showBottomDivider?: boolean
}

export function AllocationBuyFundCard({
  recommendation,
  categoryName,
  showBottomDivider = true
}: AllocationBuyFundCardProps) {
  const { openPlanRecommendationBottomSheet } = useOpenPlanRecommendationBottomSheet()
  const { allocationPlansData } = useLumpsumPlansContext()
  const { onBuy } = usePlanBuyAction({ transactionCta: allocationPlansData?.transactionCta })
  const isin = recommendation?.isin ?? ''
  const fundName = recommendation?.fundName ?? ''
  const screenName = PlansScreen.LumpsumAllocation
  const fundRole = PlansFundRole.Buy
  const buyRecommendationFormattedAmount = showAsFormattedRupeeAmount({ rawAmount: recommendation?.buyAmount ?? 0 })

  const openPlanBottomSheet = () => {
    openPlanRecommendationBottomSheet({
      isin,
      bucketType: PlanCategoryBucketKey.buyRecommendations
    })
  }

  const handleCardClick = () => {
    trackPlansFundCardOpen({ screenName, fundRole, fundName, fundIsin: isin })
    openPlanBottomSheet()
  }

  const ctas: PlanFundCardCta[] = [
    {
      id: `${isin}-action`,
      label: 'Buy',
      onClick: () => {
        trackPlansFundCardCta({ screenName, fundRole, buttonLabel: 'Buy', fundName, fundIsin: isin })
        onBuy({
          isin,
          fundName,
          amount: buyRecommendationFormattedAmount.value
        })
      },
      variant: 'tertiary'
    },
    {
      id: `${isin}-view-plan`,
      label: 'View plan',
      onClick: () => {
        trackPlansFundCardCta({ screenName, fundRole, buttonLabel: 'View plan', fundName, fundIsin: isin })
        openPlanBottomSheet()
      },
      variant: 'tertiary'
    }
  ]

  return (
    <PlansFundCard
      fund={{
        isin,
        logoUrl: recommendation?.mfLogoUrl ?? '',
        title: fundName,
        fundNature: recommendation?.fundNature ?? '',
        category: recommendation?.fundCategory ?? '',
        purchaseMode: recommendation?.purchaseMode ?? ''
      }}
      topRight={<AllocationBuyFundCardTrailing recommendation={recommendation} />}
      status={{
        rank: recommendation?.powerRank ?? 0,
        rating: recommendation?.powerRating ?? ''
      }}
      recommendationStatus={recommendation?.recommendationStatus}
      recommendationStatusDisplayText={recommendation?.recommendationStatusDisplayText}
      ctas={ctas}
      insights={buildAllocationBuyInsights(recommendation, categoryName)}
      showBottomDivider={showBottomDivider}
      onCardClick={handleCardClick}
    />
  )
}

export function buildAllocationBuyInsights(
  recommendation: LumpsumAllocationBuyRecommendation,
  categoryName: string
): PlanFundCardInsight[] {
  return buildAllocationInsightsFromTags({
    tags: recommendation?.tags,
    isin: recommendation?.isin ?? '',
    categoryName,
    // Buy: blue (INFO) stays; orange (REBALANCE) → green — same tone as rebalance buy
    resolveVisual: (defaultVisual) => toBuyAllocationInsightVisual(defaultVisual)
  })
}

export function AllocationBuyFundCardTrailing({
  recommendation
}: {
  recommendation: LumpsumAllocationBuyRecommendation
}) {
  const { allocationMovementToggle } = useLumpsumPlansContext()

  if (allocationMovementToggle === AllocationMovementToggle.FUND_MOVEMENT) {
    return <AllocationBuyFundMovementTrailing recommendation={recommendation} />
  }

  return <AllocationBuyAllocationMovementTrailing recommendation={recommendation} />
}

export function AllocationBuyFundMovementTrailing({
  recommendation
}: {
  recommendation: LumpsumAllocationBuyRecommendation
}) {
  const isNewFund = (recommendation?.currentValue ?? 0) <= 0
  const buyRecommendationFormattedAmount = showAsFormattedRupeeAmount({ rawAmount: recommendation?.buyAmount ?? 0 })

  return (
    <AllocationFundCardTrailingLayout
      primaryLabel={`Buy ${buyRecommendationFormattedAmount.label}`}
      secondaryIconHtml={
        isNewFund
          ? ALLOCATION_BUY_FUND_CARD_NEW_FUND_ICON_SVG
          : ALLOCATION_BUY_FUND_CARD_INVEST_MORE_ICON_SVG
      }
      secondaryText={isNewFund ? 'New fund' : 'Invest more'}
    />
  )
}

export function AllocationBuyAllocationMovementTrailing({
  recommendation
}: {
  recommendation: LumpsumAllocationBuyRecommendation
}) {
  const buyRecommendationFormattedAmount = showAsFormattedRupeeAmount({ rawAmount: recommendation?.buyAmount ?? 0 })
  const buyAmountLabel = `Buy ${buyRecommendationFormattedAmount.label}`

  // Normal buy direction only — current% → ideal absolute%.
  if (recommendation?.executionStatus === LumpsumAllocationCategoryExecutionStatus.ACTIONABLE_INCREASE) {
    return (
      <AllocationFundCardTrailingLayout
        primaryLabel={buyAmountLabel}
        secondaryIconHtml={ALLOCATION_BUY_FUND_CARD_PIE_ICON_SVG}
        secondaryText={`${planPercentageLabel({ rawPercentage: recommendation?.currentAllocationPercentage ?? 0 })} → ${planPercentageLabel({ rawPercentage: recommendation?.idealAllocationPercentage?.absolute ?? 0 })}`}
      />
    )
  }

  // Reverse + in-progress / completed / already-ideal — lower–upper% ideal.
  return (
    <AllocationFundCardTrailingLayout
      primaryLabel={buyAmountLabel}
      secondaryText={formatFundIdealRangeTrailingLabel(recommendation?.idealAllocationPercentage)}
    />
  )
}

export const ALLOCATION_BUY_FUND_CARD_PIE_ICON_SVG = `<svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M5.2831 1.92969C4.33555 1.92969 3.40927 2.21067 2.62141 2.7371C1.83355 3.26353 1.21949 4.01177 0.856876 4.88719C0.494263 5.76262 0.399387 6.72591 0.584246 7.65526C0.769104 8.5846 1.22539 9.43826 1.89541 10.1083C2.56544 10.7783 3.41909 11.2346 4.34844 11.4194C5.27778 11.6043 6.24108 11.5094 7.1165 11.1468C7.99192 10.7842 8.74016 10.1701 9.26659 9.38228C9.79303 8.59442 10.074 7.66815 10.074 6.7206H5.52264C5.45911 6.7206 5.39818 6.69536 5.35326 6.65044C5.30834 6.60551 5.2831 6.54458 5.2831 6.48105V1.92969Z" fill="#3D3D3D"/><path d="M6.7168 5.03955C6.7168 5.10318 6.74203 5.16411 6.78696 5.20903C6.83188 5.25395 6.89281 5.27919 6.95634 5.27919H11.5077C11.5077 4.00856 11.003 2.78998 10.1045 1.89151C9.20601 0.993036 7.98743 0.488281 6.7168 0.488281V5.03955Z" fill="#3D3D3D"/></svg>`

export const ALLOCATION_BUY_FUND_CARD_INVEST_MORE_ICON_SVG = `<svg width="12" height="12" viewBox="0 0 12.25 12.25" fill="none" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" clip-rule="evenodd" d="M6.125 1.125C3.36358 1.125 1.125 3.36357 1.125 6.125C1.125 8.8864 3.36358 11.125 6.125 11.125C8.3638 11.125 10.259 9.65355 10.8961 7.625H11.936C11.2699 10.2128 8.92075 12.125 6.125 12.125C2.81129 12.125 0.125 9.4387 0.125 6.125C0.125 2.81129 2.81129 0.125 6.125 0.125C8.92075 0.125 11.2699 2.03716 11.936 4.625H10.8961C10.259 2.59644 8.3638 1.125 6.125 1.125ZM3.79282 5.7513L6.0428 3.75129L6.7072 4.4987L5.4401 5.625H12.125V6.625H5.4401L6.7072 7.7513L6.0428 8.4987L3.79282 6.4987C3.68608 6.4038 3.625 6.2678 3.625 6.125C3.625 5.9822 3.68608 5.8462 3.79282 5.7513Z" fill="#3D3D3D" stroke="#3D3D3D" stroke-width="0.25"/></svg>`

export const ALLOCATION_BUY_FUND_CARD_NEW_FUND_ICON_SVG = `<svg width="12" height="12" viewBox="0 0 10.625 10.75" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M7.5039 3.91162C7.58415 5.5191 8.8559 6.79085 10.4634 6.8711C10.5533 6.87327 10.625 6.94675 10.625 7.03665V7.24548C10.625 7.45508 10.4543 7.6226 10.2461 7.6473C8.81431 7.81719 7.69219 8.93932 7.5223 10.3711C7.4976 10.5793 7.33008 10.75 7.12047 10.75H6.91165C6.82175 10.75 6.74827 10.6783 6.7461 10.5884C6.66947 9.05405 5.50735 7.8257 4.00387 7.6473C3.79572 7.6226 3.625 7.45508 3.625 7.24548V7.03667C3.625 6.94676 3.69674 6.87327 3.78662 6.8711C5.32095 6.79448 6.5493 5.63235 6.7277 4.12887C6.7524 3.92072 6.91992 3.75 7.12952 3.75H7.33833C7.42824 3.75 7.50173 3.82174 7.5039 3.91162ZM2.87793 0.109865C2.93246 1.20332 3.79668 2.06754 4.89015 2.12207C4.95129 2.1237 5 2.17373 5 2.23489V2.495C5 2.70487 4.82843 2.87145 4.62176 2.90792C3.74581 3.06252 3.06252 3.74581 2.90792 4.62176C2.87144 4.82843 2.70487 5 2.495 5H2.23489C2.17373 5 2.1237 4.95129 2.12207 4.89015C2.07209 3.88784 1.34179 3.07798 0.378229 2.90792C0.171555 2.87144 0 2.70487 0 2.495V2.2349C0 2.17374 0.0487201 2.1237 0.109865 2.12207C1.11216 2.07209 1.92202 1.34179 2.09208 0.378229C2.12856 0.171555 2.29513 0 2.505 0H2.7651C2.82626 0 2.8763 0.0487202 2.87793 0.109865Z" fill="#3D3D3D"/></svg>`
